import { ComponentProps, ReactNode } from 'react';

import {
  CONTEXT_NAMES,
  getOrCreateGlobalStore,
} from '@adsk/alloy-react-context';

import Notification from '../Notification';

export type Notif = Omit<ComponentProps<typeof Notification>, 'children'> & {
  message?: ReactNode;
};

export type ID = string | number;

const generateRandomID = () => Math.random().toString(36).substring(2, 15);

const NOTIFICATION_DISMISS_TIMEOUT = 6;

interface NotificationsStore {
  notifications: Notif[];
  showNotification: (notification: Notif) => {
    id: string;
    removeNotification: () => void;
  };
  removeNotification: (id: ID) => void;
}

export const useNotificationsStore = () =>
  getOrCreateGlobalStore<NotificationsStore>((set, get) => {
    const timeouts = new Map<ID, ReturnType<typeof setTimeout>>();

    return {
      notifications: [],
      showNotification: (notification: Notif) => {
        const id = notification.id || generateRandomID();

        const dismissWrapper = () => {
          get().removeNotification(id);

          if (
            notification.onDismiss &&
            typeof notification.onDismiss === 'function'
          ) {
            notification.onDismiss();
          }
        };

        const hasPromise =
          notification.asyncOperation &&
          typeof notification.asyncOperation.then === 'function';

        const finalNotification = {
          autoDismiss: NOTIFICATION_DISMISS_TIMEOUT,
          dismissible: !hasPromise,
          ...notification,
          id,
          onDismiss: dismissWrapper,
        };

        const configureTimeOut = () => {
          const timeout = setTimeout(
            finalNotification.onDismiss,
            finalNotification.autoDismiss * 1000,
          );
          timeouts.set(id, timeout);
        };

        set((state) => ({
          notifications: [...state.notifications, finalNotification],
        }));

        if (hasPromise) {
          finalNotification.asyncOperation &&
            finalNotification.asyncOperation
              .then(() => {
                configureTimeOut();
              })
              .catch(() => {
                configureTimeOut();
              });
        } else if (
          finalNotification.dismissible !== false &&
          finalNotification.autoDismiss > 0
        ) {
          configureTimeOut();
        }

        return { id, removeNotification: () => get().removeNotification(id) };
      },
      removeNotification: (id: ID) => {
        const timeout = timeouts.get(id);
        if (timeout) {
          clearTimeout(timeout);
          timeouts.delete(id);
        }
        set((state) => ({
          notifications: state.notifications.filter(
            (notification) => notification.id !== id,
          ),
        }));
      },
    };
  }, CONTEXT_NAMES.NotificationsContext)();
